export interface ProjectTreeNode<T> {
  project: T;
  children: ProjectTreeNode<T>[];
  /** True when this node has no parent in the list (top-level for move). */
  isRoot: boolean;
}

type WithIdAndParent = {
  id: string;
  parentProjectId?: string | null;
};

/** Build a forest from a flat project list using parentProjectId. */
export function buildProjectTree<T extends WithIdAndParent>(projects: T[]): ProjectTreeNode<T>[] {
  const byId = new Map<string, ProjectTreeNode<T>>();
  for (const project of projects) {
    byId.set(project.id, { project, children: [], isRoot: true });
  }

  const roots: ProjectTreeNode<T>[] = [];
  for (const node of byId.values()) {
    const parentId = String(node.project.parentProjectId || '').trim();
    if (parentId && byId.has(parentId)) {
      node.isRoot = false;
      byId.get(parentId)!.children.push(node);
    } else {
      node.isRoot = true;
      roots.push(node);
    }
  }

  const sortNodes = (nodes: ProjectTreeNode<T>[]) => {
    nodes.sort((a, b) =>
      String((a.project as { name?: string }).name || a.project.id).localeCompare(
        String((b.project as { name?: string }).name || b.project.id),
        undefined,
        { sensitivity: 'base' }
      )
    );
    for (const n of nodes) sortNodes(n.children);
  };
  sortNodes(roots);
  return roots;
}
